import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ResponseErrorWithHandled,
  ResponseSuccess,
} from '../../../../shared/types';
import { sleep } from '../../../../shared/utils/sleep';
import {
  getAccountSubscription,
  getConnectedUsersAndEmailAccounts,
  getInvoiceUrl,
  getPlans,
  purchaseSubscription,
  applyCouponCode,
  getEmailVerificationCredits,
  getEmailVerificationPlan,
  purchaseEmailVerificationCredits,
  modifySubscription,
  purchaseModifySubscription,
  reactivateSubscription,
  resumeSubscription,
  handleSubscription,
  calculatePay,
  upgradePlan,
  downgradePlan,
  purchaseLtdPlan,
  upgradeLtdPlan,
  modifyLtdPlan,
} from './helpers/billing-subscription.api';
import {
  ApplyCouponCodeRequest,
  GetInvoiceURLRequest,
  PurchaseEmailVerificationCreditsRequest,
  ModifySubscriptionRequest,
  PurchaseSubscriptionRequest,
  PurchaseModifySubscriptionRequest,
  ReactivateResumeSubscriptionRequest,
  HandleSubscriptionRequest,
  CalculatePayRequest,
  UpgradePlanRequest,
  DowngradePlanRequest,
  LTDPurchasePayload,
  LTDModifyPayload,
  LTDUpgradePayload,
} from './types';

export const getAccountSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getAccountSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await getAccountSubscription();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getPlansRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getPlansRequest', async (args, thunkAPI) => {
  try {
    return await getPlans();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getInvoiceURLRequest = createAsyncThunk<
  ResponseSuccess,
  GetInvoiceURLRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/getInvoiceURLRequest', async (args, thunkAPI) => {
  try {
    return await getInvoiceUrl(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseSubscriptionRequest', async (args, thunkAPI) => {
  try {
    const data = await purchaseSubscription(args);
    await sleep(1000);
    return data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getConnectedUsersAndEmailAccountsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>(
  'accounts/getConnectedUsersAndEmailAccountsRequest',
  async (args, thunkAPI) => {
    try {
      return await getConnectedUsersAndEmailAccounts();
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const applyCouponCodeRequest = createAsyncThunk<
  ResponseSuccess,
  ApplyCouponCodeRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/applyCouponCodeRequest', async (args, thunkAPI) => {
  try {
    return await applyCouponCode(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getEmailVerificationCreditsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('email-verification-credits', async (args, thunkAPI) => {
  try {
    return await getEmailVerificationCredits();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getEmailVerificationPlanRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('email-verification-plans', async (args, thunkAPI) => {
  try {
    return await getEmailVerificationPlan();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseEmailVerificationCreditsRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseEmailVerificationCreditsRequest,
  { rejectValue: ResponseErrorWithHandled }
>('email-verification-credits/purchase', async (args, thunkAPI) => {
  try {
    return await purchaseEmailVerificationCredits(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const modifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ModifySubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/modifySubscription', async (args, thunkAPI) => {
  try {
    return await modifySubscription(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseModifySubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  PurchaseModifySubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseModifySubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await purchaseModifySubscription(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const reactivateSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/reactivateSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await reactivateSubscription(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const resumeSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  ReactivateResumeSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/resumeSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await resumeSubscription(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const handleSubscriptionRequest = createAsyncThunk<
  ResponseSuccess,
  HandleSubscriptionRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/handleSubscriptionRequest', async (args, thunkAPI) => {
  try {
    return await handleSubscription(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const calculatePayRequest = createAsyncThunk<
  ResponseSuccess,
  CalculatePayRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/calculatePayRequest', async (args, thunkAPI) => {
  try {
    return await calculatePay(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const upgradePlanRequest = createAsyncThunk<
  ResponseSuccess,
  UpgradePlanRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/upgradePlanRequest', async (args, thunkAPI) => {
  try {
    return await upgradePlan(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const downgradePlanRequest = createAsyncThunk<
  ResponseSuccess,
  DowngradePlanRequest,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/downgradePlanRequest', async (args, thunkAPI) => {
  try {
    return await downgradePlan(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const purchaseLtdPlanRequest = createAsyncThunk<
  ResponseSuccess,
  LTDPurchasePayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/purchaseLtdPlanRequest', async (args, thunkAPI) => {
  try {
    return await purchaseLtdPlan(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const upgradeLtdPlanRequest = createAsyncThunk<
  ResponseSuccess,
  LTDUpgradePayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/upgradeLtdPlanRequest', async (args, thunkAPI) => {
  try {
    return await upgradeLtdPlan(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const modifyLtdPlanRequest = createAsyncThunk<
  ResponseSuccess,
  LTDModifyPayload,
  { rejectValue: ResponseErrorWithHandled }
>('subscription/modifyLtdPlanRequest', async (args, thunkAPI) => {
  try {
    return await modifyLtdPlan(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});
